/**
 * LaTeX Compilation Panel Component
 * Provides LaTeX compilation controls and PDF preview
 */

import React, { useState, useCallback } from 'react'
import { logError } from '@utils/logger'
import { 
  DocumentTextIcon, 
  PlayIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'

import { latexAPI, LatexCompileRequest, LatexCompilationError, LatexUtils } from '../../api/latex'

interface LatexCompilePanelProps {
  content: string
  onContentChange?: (content: string) => void
  className?: string
}

interface CompilationResult {
  success: boolean
  warnings: string[]
  errors: string[]
  pdfBlob?: Blob
  pdfUrl?: string
}

const LatexCompilePanel: React.FC<LatexCompilePanelProps> = ({
  content,
  onContentChange,
  className = ''
}) => {
  const [compiling, setCompiling] = useState(false)
  const [lastCompilation, setLastCompilation] = useState<CompilationResult | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [compilerSettings, setCompilerSettings] = useState<LatexCompileRequest>({
    content: '',
    compiler: 'pdflatex',
    runs: 2,
    include_bibtex: false
  })

  // Compile LaTeX to PDF
  const handleCompile = useCallback(async () => {
    if (!content.trim()) {
      return
    }

    setCompiling(true)
    
    try {
      const request: LatexCompileRequest = {
        ...compilerSettings,
        content: content.includes('\\documentclass') 
          ? content 
          : LatexUtils.wrapInDocument(content)
      }

      const pdfBlob = await latexAPI.compileLatexToPdf(request)
      const pdfUrl = URL.createObjectURL(pdfBlob)

      setLastCompilation({
        success: true,
        warnings: [],
        errors: [],
        pdfBlob,
        pdfUrl
      })

    } catch (error) {
      logError('Compilation error:', error)
      
      if (error instanceof LatexCompilationError) {
        setLastCompilation({
          success: false,
          warnings: error.warnings,
          errors: error.errors
        })
      } else {
        setLastCompilation({
          success: false,
          warnings: [],
          errors: [(error as Error).message || 'Unknown compilation error']
        })
      }
    } finally {
      setCompiling(false)
    }
  }, [content, compilerSettings])

  // Download PDF
  const handleDownload = useCallback(() => {
    if (lastCompilation?.pdfBlob) {
      latexAPI.downloadPdf(lastCompilation.pdfBlob, 'document.pdf')
    }
  }, [lastCompilation])


  // Insert template
  const handleInsertTemplate = useCallback((templateName: string) => {
    const template = LatexUtils.templates[templateName as keyof typeof LatexUtils.templates]
    if (template && onContentChange) {
      onContentChange(template)
    }
  }, [onContentChange])

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="w-5 h-5 text-green-600" />
            <h3 className="font-medium text-gray-900">LaTeX Tools</h3>
          </div>
          
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
            title="Advanced settings"
          >
            <Cog6ToothIcon className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Auto-compiles to PDF in preview pane. Manual compilation for download.
        </p>
      </div>

      {/* Main Controls */}
      <div className="p-4">
        <div className="flex gap-2 mb-4">
          {/* Compile Button */}
          <button
            onClick={handleCompile}
            disabled={compiling || !content.trim()}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
              ${compiling || !content.trim()
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
              }
            `}
          >
            {compiling ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Compiling...
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4" />
                Compile PDF
              </>
            )}
          </button>


          {/* Download Button */}
          {lastCompilation?.success && (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
          )}
        </div>

        {/* Advanced Settings */}
        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 p-3 bg-gray-50 rounded-lg"
            >
              <h4 className="font-medium text-gray-900 mb-3">Compilation Settings</h4>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Compiler Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Compiler
                  </label>
                  <select
                    value={compilerSettings.compiler}
                    onChange={(e) => setCompilerSettings(prev => ({ 
                      ...prev, 
                      compiler: e.target.value as any 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="pdflatex">pdfLaTeX</option>
                    <option value="xelatex">XeLaTeX</option>
                    <option value="lualatex">LuaLaTeX</option>
                  </select>
                </div>

                {/* Compilation Runs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Runs
                  </label>
                  <select
                    value={compilerSettings.runs}
                    onChange={(e) => setCompilerSettings(prev => ({ 
                      ...prev, 
                      runs: parseInt(e.target.value) 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value={1}>1 run</option>
                    <option value={2}>2 runs</option>
                    <option value={3}>3 runs</option>
                  </select>
                </div>
              </div>

              {/* BibTeX Option */}
              <div className="mt-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={compilerSettings.include_bibtex}
                    onChange={(e) => setCompilerSettings(prev => ({ 
                      ...prev, 
                      include_bibtex: e.target.checked 
                    }))}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">Include BibTeX processing</span>
                </label>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Templates */}
        <div className="mb-4">
          <h4 className="font-medium text-gray-900 mb-2">Quick Templates</h4>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(LatexUtils.templates).map(([key, template]) => (
              <button
                key={key}
                onClick={() => handleInsertTemplate(key)}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Compilation Results */}
        {lastCompilation && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              {lastCompilation.success ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
              ) : (
                <XCircleIcon className="w-5 h-5 text-red-600" />
              )}
              <h4 className="font-medium text-gray-900">
                {lastCompilation.success ? 'Compilation Successful' : 'Compilation Failed'}
              </h4>
            </div>

            {/* Warnings */}
            {lastCompilation.warnings.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Warnings ({lastCompilation.warnings.length})
                  </span>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  {lastCompilation.warnings.map((warning, index) => (
                    <div key={index} className="text-xs font-mono text-yellow-800 mb-1 last:mb-0">
                      {warning}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {lastCompilation.errors.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <XCircleIcon className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    Errors ({lastCompilation.errors.length})
                  </span>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  {lastCompilation.errors.map((error, index) => (
                    <div key={index} className="text-xs font-mono text-red-800 mb-2 last:mb-0">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default LatexCompilePanel